import React, { useState, useEffect } from "react";
import moment from "moment";
import { addDays } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { IconButton, Skeleton, Stack } from "@mui/material";

import { usePermissions } from "components/Hook";
import AddSwappedLeaves from "components/Screens/Timeoff/TimeoffAdmin/AddSwappedLeaves";
import LeaveTypeCard from "components/Screens/Timeoff/ManageTimeoff/LeaveTypeCard";
import LeavesValidityDates from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/LeavesValidityDates";
import EditLeavesTaken from "components/Screens/Timeoff/TimeoffAdmin/EditLeavesTaken";
import {
  getEmployeeLeavesDataForAdmin,
  getEmployeeLeavesDataForAdminV4,
  getEmployeesLeaveAndHolidays,
  getLastAssignmentLeaveBalance,
} from "components/Screens/Timeoff/TimeoffAdmin/TimeOffAdminServices/EmployeeTimeOffDetailsService";

import { convertDate } from "components/Screens/CommonUtils";
import { isLastAssignmentView } from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/lastAssignmentView";
import { GetAllHolidays } from "store/actions/Holidays";
import { baseUrl } from "util/APIBaseUrl";

import AddSwappedIcon from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/Icons/AddSwappedIcon";
import AddTimeOffRequest from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/Icons/AddTimeOffRequest";
import CalendarIcon from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/Icons/CalendarIcon";

import "components/Screens/Timeoff/ManageTimeoff/ManageTimeoff.scss";

const cardStyles = {
  background:
    "var(--Gradient-light, linear-gradient(221deg, #FFEFD3 2.69%, var(--color-primitives-primary-100) 41.39%, #B4FFF2 121.42%))",
  borderRadius: "12px",
  gap: "62px",
  width: "11rem",
  padding: "12px 19px",
  display: "flex",
  height: "auto",
};

const leaveCardSkeltonStyle = {
  backgroundColor: "rgb(189 192 255 / 30%)",
  borderRadius: "12px",
  width: "176px",
  height: "81px",
};

const leaveYearSkeltonStyle = {
  backgroundColor: "rgb(189 192 255 / 30%)",
  borderRadius: "26px",
  width: "62px",
  height: "28px",
};

function EmployeeTakenLeaves(props) {
  const { setLeaveTakenSuccessfully, leaveTakenSuccessfully } = props;

  const { anniversaryBasedTimeOff, futureCycleLeavesFiling } = useFlags();
  const dispatch = useDispatch();
  const { loggedInUserType } = usePermissions();

  const [employeeLeavesDataLoading, setEmployeeLeavesDataLoading] = useState(false);
  const [openRequestLeave, setOpenRequestLeave] = useState(false);
  const [openSwappedLeaves, setOpenSwappedLeaves] = useState(false);
  const [employeeLeavesData, setEmployeeLeavesData] = useState([]);
  const [allLeavesAndHolidaysData, setAllLeavesAndHolidaysData] = useState();
  const [lastAssignment, setLastAssignment] = useState(null);
  const getEmployeeDetails = useSelector((state) => state.GetEmployeeDetails.getEmployeeDetails);

  // PRD-3255: unassigned and inactive talents get a read only view of their last assignment's final balances
  const isReadOnlyView = isLastAssignmentView(getEmployeeDetails?.payload?.status);
  // LeaveTypeCard only surfaces encashments in a talent facing tooltip, so list them visibly here
  const encashedLeaves = isReadOnlyView
    ? (employeeLeavesData || []).filter((leave) => Number(leave?.encashments) > 0)
    : [];

  const renderLeaveTypeCards = () => {
    const cards = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < employeeLeavesData?.length; i++) {
      const leave = employeeLeavesData[i];

      if (anniversaryBasedTimeOff || leave?.visible || leave?.leaveType === "Swapped") {
        cards.push(
          <LeaveTypeCard
            key={i}
            portal="admin"
            style={cardStyles}
            leave={leave}
            icon={
              isReadOnlyView ? null : (
                <IconButton
                  onClick={() => setOpenSwappedLeaves(true)}
                  sx={{ "&.MuiIconButton-root": { padding: "0px" } }}
                >
                  <AddSwappedIcon />
                </IconButton>
              )
            }
          />
        );
      }
    }

    if (isReadOnlyView && !cards.length) {
      return (
        <div className="timeoff-valid" data-testid="last-assignment-empty">
          No leave balance available for the last assignment
        </div>
      );
    }

    return (
      <div className="leave-type-cards-container" style={{ alignItems: "center" }}>
        {cards}

        {!isReadOnlyView && (
          <div
            className="edit-leave-type-taken"
            style={{ alignItems: "center" }}
            onClick={() => setOpenRequestLeave(true)}
          >
            <div className="add-timeoff-req">Add time-off request</div>
            <IconButton sx={{ ml: "20px", padding: "0px" }}>
              <AddTimeOffRequest />
            </IconButton>
          </div>
        )}
      </div>
    );
  };

  const fetchLastAssignmentLeaveBalance = async () => {
    setEmployeeLeavesDataLoading(true);
    try {
      const res = await getLastAssignmentLeaveBalance(getEmployeeDetails?.payload?.employeeId);
      setLastAssignment(res?.data?.assignment || null);
      setEmployeeLeavesData(res?.data?.leaves || []);
    } catch (error) {
      setLastAssignment(null);
      setEmployeeLeavesData([]);
    } finally {
      setEmployeeLeavesDataLoading(false);
    }
  };

  const fetchEmployeeLeavesDataForAdmin = async () => {
    setEmployeeLeavesDataLoading(true);
    try {
      const res = futureCycleLeavesFiling
        ? await getEmployeeLeavesDataForAdminV4(getEmployeeDetails?.payload?.employeeId)
        : await getEmployeeLeavesDataForAdmin(getEmployeeDetails?.payload?.employeeId, anniversaryBasedTimeOff);
      setEmployeeLeavesDataLoading(false);
      setEmployeeLeavesData(res?.data);
    } catch (error) {
      setEmployeeLeavesDataLoading(false);
    }
  };

  const fetchEmployeeLeavesAndHolidaysData = async () => {
    try {
      const startDate = moment().startOf("year").format("YYYY-MM-DD");
      const endDate = moment().endOf("year").format("YYYY-MM-DD");

      const res = await getEmployeesLeaveAndHolidays(getEmployeeDetails?.payload?.employeeId, startDate, endDate);
      setAllLeavesAndHolidaysData(res?.result);
    } catch (error) {
      console.log(error);
    }
  };

  const onSuccessLeaveTaken = () => {
    setLeaveTakenSuccessfully(!leaveTakenSuccessfully);
    fetchEmployeeLeavesDataForAdmin();
    if (loggedInUserType == "edge" && getEmployeeDetails?.payload?.employeeId) {
      dispatch(
        GetAllHolidays({
          request: `${baseUrl}/employee/${getEmployeeDetails?.payload?.employeeId}/leave_holidays`,
          region: getEmployeeDetails?.payload?.country,
          startDate: loggedInUserType == "edge" ? moment().startOf("year").format("YYYY-MM-DD") : new Date(),
          endDate: loggedInUserType == "edge" ? moment().endOf("year").format("YYYY-MM-DD") : addDays(new Date(), 365),
        })
      );
    }
  };

  useEffect(() => {
    if (getEmployeeDetails?.payload?.employeeId) {
      if (isReadOnlyView) {
        fetchLastAssignmentLeaveBalance();
        return;
      }
      fetchEmployeeLeavesDataForAdmin();
      fetchEmployeeLeavesAndHolidaysData();
    }
  }, [getEmployeeDetails]);

  return (
    <Stack
      spacing="12px"
      sx={{
        mt: "-1px",
        border: "1px solid var(--color-primitives-primary-50)",
        background: "var(--primary-99, #FBFAFF)",
        padding: "28px 60px",
      }}
    >
      <div style={{ maxWidth: "max-content" }}>
        <Stack
          direction="row"
          spacing="12px"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            mb: "5px"
          }}>
          <Stack direction="row" spacing="12px" sx={{
            alignItems: "start"
          }}>
            <CalendarIcon />
            <div className="timeoff-subheading">Leaves taken</div>
            {employeeLeavesDataLoading ? (
              <Skeleton variant="rounded" sx={{ ...leaveYearSkeltonStyle }} />
            ) : (
              <div className="timeoff-year"> {employeeLeavesData?.[0]?.year || "-"}</div>
            )}
          </Stack>
          {employeeLeavesData?.length > 0 && employeeLeavesData?.[0]?.startDate && employeeLeavesData?.[0]?.endDate && (
            <LeavesValidityDates
              startDate={employeeLeavesData?.[0]?.startDate}
              endDate={employeeLeavesData?.[0]?.endDate}
            />
          )}
        </Stack>

        {isReadOnlyView && lastAssignment && !employeeLeavesDataLoading && (
          <div
            className="timeoff-valid"
            style={{ marginBottom: encashedLeaves.length ? "4px" : "12px" }}
            data-testid="last-assignment-context"
          >
            Last assignment: <span className="timeoff-valid-date">{lastAssignment?.companyName || "-"}</span>
            {lastAssignment?.endDate && (
              <>
                , ended <span className="timeoff-valid-date">{convertDate(lastAssignment.endDate)}</span>
              </>
            )}
          </div>
        )}

        {isReadOnlyView && encashedLeaves.length > 0 && !employeeLeavesDataLoading && (
          <div className="timeoff-valid" style={{ marginBottom: "12px" }} data-testid="last-assignment-encashments">
            Encashed at end of assignment:{" "}
            <span className="timeoff-valid-date">
              {encashedLeaves
                .map(({ leaveType, encashments }) => {
                  const days = Number(encashments);
                  return `${leaveType} ${days} ${days === 1 ? "day" : "days"}`;
                })
                .join(", ")}
            </span>
          </div>
        )}

        {employeeLeavesDataLoading ? (
          <Stack direction="row" sx={{
            gap: "1rem"
          }}>
            <Skeleton variant="rounded" sx={{ ...leaveCardSkeltonStyle }} />
            <Skeleton variant="rounded" sx={{ ...leaveCardSkeltonStyle }} />
            <Skeleton variant="rounded" sx={{ ...leaveCardSkeltonStyle }} />
          </Stack>
        ) : (
          renderLeaveTypeCards()
        )}
      </div>
      {openSwappedLeaves && !isReadOnlyView && (
        <AddSwappedLeaves
          open={openSwappedLeaves}
          closeModal={() => setOpenSwappedLeaves(false)}
          employeeLeavesData={employeeLeavesData}
          onSuccess={onSuccessLeaveTaken}
        />
      )}
      {openRequestLeave && !isReadOnlyView && (
        <EditLeavesTaken
          open={openRequestLeave}
          closeModal={() => setOpenRequestLeave(false)}
          employeeLeavesData={employeeLeavesData}
          onSuccess={onSuccessLeaveTaken}
          allLeavesAndHolidaysData={allLeavesAndHolidaysData}
        />
      )}
    </Stack>
  );
}

export default EmployeeTakenLeaves;
